/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ReactNode } from "react";

/**
 * Stable identity for a card action, so screens can key focus handling and
 * dispatch on an enum instead of loose string literals. `TContentCardAction.id`
 * also accepts a plain string for screen-specific actions not listed here.
 */
export enum ContentCardActionId {
  PLAY = "play",
  DETAILS = "details",
  DELETE = "delete",
  UNSAVE = "unsave",
  SAVE = "save",
  UNDELETE = "undelete",
  PERMANENTLY_DELETE = "permanentlyDelete",
  STOP = "stop"
}

/**
 * Where a card's thumbnail badge sits.
 *
 * `TOP_RIGHT` is the compact default, out of the way of artwork subjects.
 * `BOTTOM_CENTER` is the wide pill form used by the My Hub Saved tab
 * (Figma: My Hub Detail — node 115:1069), which reads as a caption on the
 * thumbnail rather than a corner tag.
 */
export enum ContentCardBadgePlacement {
  TOP_RIGHT = "top-right",
  BOTTOM_CENTER = "bottom-center"
}

/**
 * One action button rendered in a card's action row.
 *
 * Icon-only by default. Set {@link TContentCardAction.hasVisibleLabel} for the
 * wider icon-plus-text form (Figma: "10ft / Button / Action / Square").
 *
 * The card renders these purely — it never derives behaviour from `id`; the
 * consuming screen receives the id back through
 * {@link ContentCardProps.onActionSelect} and decides what to do.
 */
export interface TContentCardAction {
  /** Identity handed back to `onActionSelect`. Also the React key. */
  id: ContentCardActionId | string;
  /**
   * Accessible name, used for `aria-label` and the native tooltip, and as the
   * visible text when {@link TContentCardAction.hasVisibleLabel} is set. Always
   * required, because the icon-only form has no other accessible name. Must
   * already be localized — `views/common` stays locale-agnostic.
   */
  label: string;
  /** The inline SVG icon element. See `ContentCardIcons`. */
  icon: ReactNode;
  /**
   * Renders `label` as visible text beside the icon, in a pill-shaped button
   * that hugs its content instead of the icon-only circle. Defaults to `false`.
   */
  hasVisibleLabel?: boolean;
  /**
   * Renders the high-emphasis filled variant — one per row at most, for the
   * row's default action (Figma: the filled "Play" button).
   */
  isPrimary?: boolean;
  /** Renders the button non-interactive and dimmed. */
  isDisabled?: boolean;
}

/**
 * Presentation-ready view of a single item.
 *
 * Deliberately free of any feed or domain type so the card can be dropped into
 * any list, grid or panel. Callers holding a `TCarouselItem` should map it with
 * `toContentCardModel` from `viewModel/utility`.
 */
export interface TContentCardModel {
  /** Unique id for the item — used by callers for keys, not by the card. */
  id: string;
  /** Primary heading. */
  title: string;
  /** Secondary line under the title — e.g. "S1 E4 - Pilot". */
  subtitle?: string;
  /**
   * A single highlighted line rendered below `subtitle`, in the card's accent
   * colour — for time-sensitive information the eye should catch first, such as
   * "Expires on 6/17/2026". Screens can retint it by setting the
   * `--content-card-accent-color` custom property on the card's `className`.
   */
  accentText?: string;
  /**
   * Metadata fragments joined by a middot separator by the card,
   * e.g. `["TV-14", "45 min", "2024"]`. Empty entries are dropped.
   */
  metadata?: readonly string[];
  /** Body copy, clamped to {@link ContentCardProps.descriptionMaxLines}. */
  description?: string;
  /** Thumbnail source. When empty, a title-text fallback is rendered. */
  imageUrl?: string;
  /**
   * Watched progress as a percentage `0`–`100`. Values at or below `0` hide
   * the progress sliver. Matches `TCarouselItem.progressPercent`.
   */
  progressPercent?: number;
  /** Badge text overlaid on the thumbnail, e.g. "LIVE" or "NEW". */
  badgeText?: string;
  /**
   * Where `badgeText` sits on the thumbnail. Defaults to
   * {@link ContentCardBadgePlacement.TOP_RIGHT}.
   */
  badgePlacement?: ContentCardBadgePlacement;
  /**
   * Status flags for the thumbnail's badge strip, drawn by the shared
   * `TileBadges` — the same strip, in the same priority order, that a carousel
   * tile shows. Independent of `badgeText`, which is the card's own worded
   * caption: a row can carry both (My Hub's Saved rows show the SAVED badge
   * top-left and a "Saved ‧ 1 hour" caption along the bottom).
   *
   * Recording renders as the red dot rather than a worded chip.
   */
  isLive?: boolean;
  /** @see ContentCardProps.isLive */
  isUpcoming?: boolean;
  /** @see ContentCardProps.isLive */
  isNew?: boolean;
  /** @see ContentCardProps.isLive */
  isSaved?: boolean;
  /** @see ContentCardProps.isLive */
  isWatched?: boolean;
  /** @see ContentCardProps.isLive */
  isRecording?: boolean;
}

/**
 * Props for {@link ContentCard}.
 *
 * The card is fully presentational: it holds no state and never calls
 * `element.focus()`. Focus is owned by the consuming screen's Redux slice and
 * arrives through `isFocused` / `focusedActionIndex`, matching the convention
 * used by `FilterStrip` and `ActionPill`.
 */
export interface ContentCardProps {
  /** The item to render. */
  item: TContentCardModel;
  /**
   * Actions rendered left-to-right in array order. Omitted or empty means no
   * action row is rendered at all.
   */
  actions?: readonly TContentCardAction[];
  /** True when this card holds D-pad focus. */
  isFocused?: boolean;
  /**
   * Index into `actions` of the focused action. `-1` or omitted means focus
   * sits on the card body rather than on an action.
   */
  focusedActionIndex?: number;
  /**
   * Called when the card takes focus. Wire to `ContentGridItemContext.onFocus`;
   * the card fires it on both native focus and pointer hover so D-pad and mouse
   * behave identically.
   */
  onFocus?: () => void;
  /**
   * Called on primary activation of the card body. Wire to
   * `ContentGridItemContext.onSelect`. D-pad OK is dispatched by the screen's
   * `ScreenController`, not by this component.
   */
  onSelect?: () => void;
  /** Called when an action button is activated by pointer. */
  onActionSelect?: (actionId: string, index: number) => void;
  /**
   * Reveals the action row even while the card is unfocused. Default behaviour
   * (TV) only reveals actions on focus or hover.
   */
  areActionsAlwaysVisible?: boolean;
  /** Lines to clamp `description` to. Defaults to `2`. */
  descriptionMaxLines?: number;
  /** Extra class on the card root, for call-site spacing. */
  className?: string;
  /** Overrides the root `data-testid`. Defaults to `"content-card"`. */
  dataTestId?: string;
}
